import React, { useState } from 'react';
import { Search, ChevronDown, X, Coins } from 'lucide-react';
import { AdvancedFilters, QuickFilters } from '../hooks/useInvoiceFiltering';
import { getShortcutHint } from '../hooks/useKeyboardShortcuts';

interface EnhancedFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  quickFilters: QuickFilters;
  onQuickFilterToggle: (filter: keyof QuickFilters) => void;
  advancedFilters: AdvancedFilters;
  onAdvancedFiltersChange: (filters: AdvancedFilters) => void;
  activeFilterCount: number;
  onClearAll: () => void;
}

/** Date input <-> Date, via the yyyy-mm-dd string the input expects. */
const toInputDate = (value: Date | null): string =>
  value ? new Date(value).toISOString().slice(0, 10) : '';

const fromInputDate = (value: string): Date | null =>
  value ? new Date(`${value}T00:00:00`) : null;

const toInputNumber = (value: number | null): string =>
  value === null || Number.isNaN(value) ? '' : String(value);

const fromInputNumber = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const EnhancedFilters: React.FC<EnhancedFiltersProps> = ({
  searchTerm,
  onSearchChange,
  quickFilters,
  onQuickFilterToggle,
  advancedFilters,
  onAdvancedFiltersChange,
  activeFilterCount,
  onClearAll
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const setDateRange = (key: 'start' | 'end', value: string) =>
    onAdvancedFiltersChange({
      ...advancedFilters,
      dateRange: { ...advancedFilters.dateRange, [key]: fromInputDate(value) },
    });

  const setAmountRange = (key: 'min' | 'max', value: string) =>
    onAdvancedFiltersChange({
      ...advancedFilters,
      amountRange: { ...advancedFilters.amountRange, [key]: fromInputNumber(value) },
    });

  return (
    <div className="space-y-4 p-6 bg-black/20 backdrop-blur-md rounded-2xl border border-coral-500/20">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-foreground-muted pointer-events-none" />
        <input
          type="search"
          data-search-input
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={`Search invoices, sellers, buyers, or amounts... (${getShortcutHint('search')})`}
          className="w-full h-11 pl-11 pr-4 rounded-lg border border-coral-500/15 bg-coral-500/5
                     text-base text-foreground placeholder:text-foreground-muted
                     focus:border-coral-500/40 focus:bg-coral-500/10 focus:ring-4 focus:ring-coral-500/10
                     transition-all duration-200 outline-none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          aria-label="Search invoices"
          role="searchbox"
        />
      </div>

      {/* Quick Filter Pills */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onQuickFilterToggle('overdue')}
          className={`h-8 px-4 rounded-full border-[1.5px] text-sm font-medium
                     transition-all duration-200
                     ${quickFilters.overdue
                       ? 'bg-coral-500 border-coral-500 text-white font-semibold'
                       : 'border-coral-500/20 text-foreground-muted hover:bg-coral-500/5 hover:border-coral-500/30'
                     }`}
          aria-label="Filter by overdue status"
          aria-pressed={quickFilters.overdue}
        >
          Overdue
        </button>
        <button
          onClick={() => onQuickFilterToggle('dueThisWeek')}
          className={`h-8 px-4 rounded-full border-[1.5px] text-sm font-medium
                     transition-all duration-200
                     ${quickFilters.dueThisWeek
                       ? 'bg-coral-500 border-coral-500 text-white font-semibold'
                       : 'border-coral-500/20 text-foreground-muted hover:bg-coral-500/5 hover:border-coral-500/30'
                     }`}
          aria-label="Filter by due this week"
          aria-pressed={quickFilters.dueThisWeek}
        >
          Due This Week
        </button>
        <button
          onClick={() => onQuickFilterToggle('highValue')}
          className={`h-8 px-4 rounded-full border-[1.5px] text-sm font-medium
                     transition-all duration-200
                     ${quickFilters.highValue
                       ? 'bg-coral-500 border-coral-500 text-white font-semibold'
                       : 'border-coral-500/20 text-foreground-muted hover:bg-coral-500/5 hover:border-coral-500/30'
                     }`}
          title="Amount over 10,000, compared without converting between currencies"
          aria-label="Filter by amount over 10,000"
          aria-pressed={quickFilters.highValue}
        >
          Amount &gt; 10,000
        </button>

        <button
          onClick={() => onQuickFilterToggle('uploaded')}
          className={`h-8 px-4 rounded-full border-[1.5px] text-sm font-medium
                     transition-all duration-200
                     ${quickFilters.uploaded
                       ? 'bg-blue-500 border-blue-500 text-white font-semibold'
                       : 'border-coral-500/20 text-foreground-muted hover:bg-coral-500/5 hover:border-coral-500/30'
                     }`}
          aria-label="Filter by uploaded documents"
          aria-pressed={quickFilters.uploaded}
        >
          Uploaded
        </button>
        <button
          onClick={() => onQuickFilterToggle('created')}
          className={`h-8 px-4 rounded-full border-[1.5px] text-sm font-medium
                     transition-all duration-200
                     ${quickFilters.created
                       ? 'bg-coral-500 border-coral-500 text-white font-semibold'
                       : 'border-coral-500/20 text-foreground-muted hover:bg-coral-500/5 hover:border-coral-500/30'
                     }`}
          aria-label="Filter by created invoices"
          aria-pressed={quickFilters.created}
        >
          Created
        </button>

        {/* Clear All Button (only show when filters are active) */}
        {activeFilterCount > 0 && (
          <button
            onClick={onClearAll}
            className="h-8 px-4 rounded-full border-[1.5px] border-red-500/30 text-sm font-medium
                       text-red-300 hover:bg-red-500/10 hover:border-red-500/50
                       transition-all duration-200 flex items-center gap-1"
            aria-label="Clear all filters"
          >
            <X className="w-3 h-3" />
            Clear All ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Advanced Filters Toggle */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-2 py-2 text-sm font-medium text-foreground-muted
                   hover:text-foreground-secondary transition-colors"
        aria-expanded={showAdvanced}
        aria-controls="advanced-filters-panel"
      >
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${
            showAdvanced ? 'rotate-180' : ''
          }`}
        />
        Advanced Filters
      </button>

      {/* Advanced Filters Panel */}
      {showAdvanced && (
        <div
          id="advanced-filters-panel"
          className="mt-4 p-4 rounded-lg bg-coral-500/5 border border-coral-500/10 space-y-4
                     animate-fadeInUp"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wide mb-2">
                Date Range
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  className="flex-1 h-9 px-3 rounded-md border border-coral-500/15 bg-coral-500/5
                             text-sm text-foreground
                             focus:border-coral-500/40 focus:bg-coral-500/10 focus:ring-2 focus:ring-coral-500/10
                             transition-all duration-200 outline-none"
                  value={toInputDate(advancedFilters.dateRange.start)}
                  onChange={(e) => setDateRange('start', e.target.value)}
                  aria-label="Start date"
                />
                <input
                  type="date"
                  className="flex-1 h-9 px-3 rounded-md border border-coral-500/15 bg-coral-500/5
                             text-sm text-foreground
                             focus:border-coral-500/40 focus:bg-coral-500/10 focus:ring-2 focus:ring-coral-500/10
                             transition-all duration-200 outline-none"
                  value={toInputDate(advancedFilters.dateRange.end)}
                  onChange={(e) => setDateRange('end', e.target.value)}
                  aria-label="End date"
                />
              </div>
            </div>

            {/* Amount Range */}
            <div>
              <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wide mb-2">
                Amount Range
              </label>
              <div className="flex gap-2 items-center">
                <Coins className="w-4 h-4 text-coral-400 flex-shrink-0" />
                <input
                  type="number"
                  className="flex-1 h-9 px-3 rounded-md border border-coral-500/15 bg-coral-500/5
                             text-sm text-foreground
                             focus:border-coral-500/40 focus:bg-coral-500/10 focus:ring-2 focus:ring-coral-500/10
                             transition-all duration-200 outline-none"
                  value={toInputNumber(advancedFilters.amountRange.min)}
                  onChange={(e) => setAmountRange('min', e.target.value)}
                  placeholder="Min"
                  min="0"
                  step="100"
                  aria-label="Minimum amount"
                />
                <span className="text-coral-400">to</span>
                <Coins className="w-4 h-4 text-coral-400 flex-shrink-0" />
                <input
                  type="number"
                  className="flex-1 h-9 px-3 rounded-md border border-coral-500/15 bg-coral-500/5
                             text-sm text-foreground
                             focus:border-coral-500/40 focus:bg-coral-500/10 focus:ring-2 focus:ring-coral-500/10
                             transition-all duration-200 outline-none"
                  value={toInputNumber(advancedFilters.amountRange.max)}
                  onChange={(e) => setAmountRange('max', e.target.value)}
                  placeholder="Max"
                  min="0"
                  step="100"
                  aria-label="Maximum amount"
                />
              </div>
            </div>
          </div>

          {/* Status Filters */}
          <div>
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wide mb-2">
              Invoice Status
            </label>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'readyToProcess', label: 'Ready to Process' },
                { key: 'confirmed', label: 'Confirmed' },
                { key: 'sent', label: 'Sent' },
                { key: 'paid', label: 'Paid' },
                { key: 'overdue', label: 'Overdue' },
              ] as const).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => onQuickFilterToggle(key)}
                  className={`h-8 px-3 rounded-full border-[1.5px] text-sm font-medium transition-all duration-200 ${
                    quickFilters[key]
                      ? 'bg-coral-500 border-coral-500 text-white font-semibold'
                      : 'border-coral-500/20 text-foreground-muted hover:bg-coral-500/5 hover:border-coral-500/30'
                  }`}
                  aria-pressed={quickFilters[key]}
                  aria-label={`Filter by ${label}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeInUp {
          animation: fadeInUp 300ms ease-out;
        }
      `}</style>
    </div>
  );
};

export default EnhancedFilters;
